// «إزاي تبدأ». A pinned 2×2 board on the left and the heading on the right; as
// the page scrolls, a night tile travels the cells (1 top right, 2 top left,
// 3 bottom left, 4 bottom right). REFERENCE.md §9, spec section 4.
//
// HOW has three steps, and the board has four cells: the fourth carries
// «ولو وقفت؟» under an icon disc, never an invented step. Those three lines read
// as one answer, so if a number is unknown they all go (same rule as
// Pages/Home/HowItWorks.js) and the cell says the same thing without numbers.
// It used to become the sign-up button, which looked like a broken card.
//
// Pinned at 1024 and up, only when the block fits under the compact header, and
// not under reduced motion. Otherwise there is no pin and no run, and the tile
// marks the cell nearest the middle of the screen.

import { useEffect, useLayoutEffect, useRef, useState } from "react";
import CtaLink from "./CtaLink";
import { CirclePause } from "lucide-react";
import { HOW, fillVars, signupHref } from "../Home/content";
import SectionHead from "./SectionHead";
import { onScrollFrame } from "./motion/env";
import usePinnedSteps from "./motion/usePinnedSteps";
import "./Method.css";

// Absolute pixels on purpose: the run does not scale with the window.
const STEP_PX = 400;

const compactHeaderPx = () => {
    const root = getComputedStyle(document.documentElement);
    return parseFloat(root.getPropertyValue("--ld-header-h-compact")) * parseFloat(root.fontSize);
};

export default function Method({ how, vars, ctaHref: href }) {
    const steps = how.steps || [];
    const filled = (how.whatIf || []).map((line) => fillVars(line, vars));
    const whatIf = filled.length && filled.every(Boolean) ? filled.join(" ") : HOW.whatIfPlain;
    const ctaHref = href || signupHref();

    const cellCount = Math.max(steps.length, 4);

    const trackRef = useRef(null);
    const stickyRef = useRef(null);
    const gridRef = useRef(null);
    const tileRef = useRef(null);

    const { active: pinStep, pinned: canPin } = usePinnedSteps(trackRef, {
        stickyRef,
        count: cellCount,
        stepPx: STEP_PX,
    });

    const [fits, setFits] = useState(true);
    const [nearest, setNearest] = useState(0);
    const isPinned = canPin && fits;
    const active = isPinned ? pinStep : nearest;

    // The block sits centred under the compact header, so its sticky top depends
    // on its own height, which depends on the copy.
    useLayoutEffect(() => {
        const sticky = stickyRef.current;
        if (!sticky) return undefined;
        const measure = () => {
            const h = sticky.getBoundingClientRect().height;
            sticky.style.setProperty("--ld-method-h", `${h}px`);
            setFits(window.innerHeight - compactHeaderPx() - h >= 0);
        };
        measure();
        const ro = new ResizeObserver(measure);
        ro.observe(sticky);
        window.addEventListener("resize", measure);
        return () => {
            ro.disconnect();
            window.removeEventListener("resize", measure);
        };
    }, []);

    // Without the pin, the active cell is the one nearest the screen's middle.
    useEffect(() => {
        if (isPinned) return undefined;
        const pick = () => {
            const cells = gridRef.current ? Array.from(gridRef.current.children) : [];
            const mid = window.innerHeight / 2;
            let best = 0;
            let bestGap = Infinity;
            cells.forEach((cell, i) => {
                const r = cell.getBoundingClientRect();
                const gap = Math.abs(r.top + r.height / 2 - mid);
                if (gap < bestGap) {
                    bestGap = gap;
                    best = i;
                }
            });
            setNearest(best);
        };
        pick();
        return onScrollFrame(pick);
    }, [isPinned]);

    // The tile covers the active cell exactly. It moves on its own clock (.45s),
    // not with the scroll, and appears in place rather than sliding in from 0,0.
    useLayoutEffect(() => {
        const grid = gridRef.current;
        const tile = tileRef.current;
        if (!grid || !tile) return undefined;
        const place = () => {
            const cell = grid.children[active];
            if (!cell) return;
            const g = grid.getBoundingClientRect();
            const c = cell.getBoundingClientRect();
            tile.style.width = `${c.width}px`;
            tile.style.height = `${c.height}px`;
            tile.style.translate = `${c.left - g.left}px ${c.top - g.top}px`;
        };
        place();
        const raf = requestAnimationFrame(() => tile.classList.add("is-placed"));
        const ro = new ResizeObserver(place);
        ro.observe(grid);
        return () => {
            cancelAnimationFrame(raf);
            ro.disconnect();
        };
    }, [active]);

    return (
        <section
            className={`ld-section ld-section--day ld-method ${isPinned ? "is-pinned" : ""}`.trim()}
            id="how"
            aria-labelledby="ld-method-title"
        >
            <div className="ld-method__track" ref={trackRef}>
                <div className="ld-method__sticky" ref={stickyRef}>
                    <div className="ld-container ld-method__inner">
                        <SectionHead
                            id="ld-method-title"
                            className="ld-method__head"
                            flat
                            eyebrow={how.eyebrow}
                            title={how.title}
                            lead={how.subtitle}
                            aside={<CtaLink href={ctaHref} className="ld-btn ld-btn--secondary">{how.ctaText}</CtaLink>}
                        />
                        <div className="ld-method__board ld-glass-day-lite" data-ld="steps">
                            <div className="ld-method__tile" ref={tileRef} aria-hidden="true" />
                            <ol className="ld-method__grid" ref={gridRef}>
                                {steps.map((step, i) => (
                                    <li
                                        key={step.title}
                                        className={`ld-method__step ${active === i ? "is-active" : ""}`.trim()}
                                        aria-current={active === i ? "step" : undefined}
                                        style={{ "--i": i }}
                                    >
                                        <span className="ld-disc ld-disc--sm ld-method__num">{i + 1}</span>
                                        <h3 className="ld-method__title">{step.title}</h3>
                                        <p className="ld-small ld-method__line">{step.body}</p>
                                    </li>
                                ))}
                                {steps.length < 4 ? (
                                    <li
                                        className={`ld-method__step ld-method__step--note ${active === steps.length ? "is-active" : ""}`.trim()}
                                        aria-current={active === steps.length ? "step" : undefined}
                                        style={{ "--i": steps.length }}
                                    >
                                        <span className="ld-disc ld-disc--sm"><CirclePause aria-hidden="true" /></span>
                                        <h3 className="ld-method__title">{how.whatIfTitle || HOW.whatIfTitle}</h3>
                                        <p className="ld-small ld-method__line">{whatIf}</p>
                                    </li>
                                ) : null}
                            </ol>
                        </div>
                    </div>
                </div>
                <div
                    className="ld-method__run"
                    style={{ "--ld-method-run": `${cellCount * STEP_PX}px` }}
                    aria-hidden="true"
                />
            </div>
        </section>
    );
}
